/**
 * Many units at a glance, for the screen the accountant lives on.
 *
 * One unit's figures come from its entries. Six hundred units at once cannot,
 * which is what the unitPeriods projection is for, so this composes a rollup
 * per unit with the two facts a rollup cannot know: whether the unit is being
 * billed at all, and whether a payment is sitting in the queue. The state each
 * tile shows is decided by the same function the resident's own screen uses,
 * so the two can never disagree about what a month means.
 */

import type { Birr } from "./money.js";
import { type PeriodSummary, type UnitState, unitStateFor } from "./ledger.js";
import { type Period, comparePeriods, periodsBetween } from "./period.js";

export interface UnitRow {
  unitId: string;
  block: string;
  /** The occupancy the unit is on now, which is the one a tile speaks for. */
  termId: string;
  active: boolean;
  billingPaused: boolean;
}

export interface UnitTile {
  unitId: string;
  block: string;
  termId: string;
  state: UnitState;
  /** What the unit still owes for the month on screen. */
  balance: Birr;
}

export interface UnitMap {
  tiles: UnitTile[];
  counts: Record<UnitState, number>;
  /** Every block in the organization, in door order, whatever the filter is. */
  blocks: string[];
  owed: Birr;
}

const CHUNKS = /\d+|\D+/g;

/**
 * Door order, not string order. A plain sort puts B10 before B2, which is
 * wrong on a wall of tiles somebody is reading like a floor plan.
 */
export function compareUnitIds(a: string, b: string): number {
  const left = a.toUpperCase().match(CHUNKS) ?? [];
  const right = b.toUpperCase().match(CHUNKS) ?? [];

  for (let index = 0; index < Math.min(left.length, right.length); index += 1) {
    const one = left[index]!;
    const two = right[index]!;
    const bothNumeric = /^\d/.test(one) && /^\d/.test(two);
    if (bothNumeric) {
      const difference = Number(one) - Number(two);
      if (difference !== 0) {
        return difference;
      }
    } else if (one !== two) {
      return one < two ? -1 : 1;
    }
  }
  return left.length - right.length;
}

const NO_COUNTS: Record<UnitState, number> = {
  unpaid: 0,
  unverified: 0,
  verified: 0,
  partial: 0,
  inactive: 0,
};

/**
 * Counts describe exactly the tiles returned. Filtering to a block and then
 * showing counts for the whole building would make the filter chips lie about
 * what is on screen.
 */
export function unitMap(options: {
  units: readonly UnitRow[];
  /** The month's rollup per unit. A unit with no rollup was never billed. */
  summaries: ReadonlyMap<string, PeriodSummary>;
  /** Units with a payment waiting on the accountant, whatever month it covers. */
  pending: ReadonlySet<string>;
  block?: string;
}): UnitMap {
  const { units, summaries, pending, block } = options;

  const blocks = [...new Set(units.map((unit) => unit.block))].sort(compareUnitIds);
  const counts = { ...NO_COUNTS };
  let owed = 0;

  const tiles = units
    .filter((unit) => block === undefined || unit.block === block)
    .map((unit) => {
      const summary = summaries.get(unit.unitId);
      const state = unitStateFor({
        summary,
        hasPendingPayment: pending.has(unit.unitId),
        active: unit.active,
        billingPaused: unit.billingPaused,
      });
      counts[state] += 1;
      owed += summary?.balance ?? 0;
      return {
        unitId: unit.unitId,
        block: unit.block,
        termId: unit.termId,
        state,
        balance: summary?.balance ?? 0,
      };
    })
    .sort((one, two) => compareUnitIds(one.unitId, two.unitId));

  return { tiles, counts, blocks, owed };
}

/** How old the oldest month a unit still owes is, in the buckets a committee asks about. */
export type AgeBucket = "current" | "one" | "two" | "threePlus";

export const AGE_BUCKETS: AgeBucket[] = ["current", "one", "two", "threePlus"];

export interface ArrearsRow {
  unitId: string;
  block: string;
  /**
   * The occupancy the debt belongs to, so a row can open the unit without
   * going back to the map to find out which term it is on.
   */
  termId: string;
  /** Everything the unit owes, across every month it owes for. */
  owed: Birr;
  /** How many months are still owing, which is not the same as how old they are. */
  months: number;
  oldest: Period;
  bucket: AgeBucket;
}

export interface Arrears {
  rows: ArrearsRow[];
  buckets: Record<AgeBucket, { units: number; owed: Birr }>;
  owed: Birr;
}

function bucketFor(age: number): AgeBucket {
  if (age <= 0) {
    return "current";
  }
  if (age === 1) {
    return "one";
  }
  return age === 2 ? "two" : "threePlus";
}

const NO_BUCKETS = (): Record<AgeBucket, { units: number; owed: Birr }> => ({
  current: { units: 0, owed: 0 },
  one: { units: 0, owed: 0 },
  two: { units: 0, owed: 0 },
  threePlus: { units: 0, owed: 0 },
});

/**
 * Who is behind, worst first.
 *
 * A unit is aged by its oldest unpaid month, not by how many it owes. Three
 * months missed in a row and three months missed last year are different
 * problems, and the committee chases them differently.
 *
 * Only months that still owe something are passed in, so a building with a year
 * of history costs a query the size of its arrears rather than the size of its
 * books.
 */
export function arrears(options: {
  outstanding: readonly { unitId: string; period: Period; owed: Birr }[];
  blocks: ReadonlyMap<string, string>;
  /** Each unit's current occupancy, keyed the same way as the blocks. */
  terms?: ReadonlyMap<string, string>;
  currentPeriod: Period;
  block?: string;
}): Arrears {
  const { outstanding, blocks, terms, currentPeriod, block } = options;

  const byUnit = new Map<string, { owed: Birr; months: number; oldest: Period }>();
  for (const row of outstanding) {
    if (row.owed <= 0) {
      continue;
    }
    const unitBlock = blocks.get(row.unitId);
    if (block !== undefined && unitBlock !== block) {
      continue;
    }
    const unit = byUnit.get(row.unitId);
    if (!unit) {
      byUnit.set(row.unitId, { owed: row.owed, months: 1, oldest: row.period });
      continue;
    }
    unit.owed += row.owed;
    unit.months += 1;
    if (comparePeriods(row.period, unit.oldest) < 0) {
      unit.oldest = row.period;
    }
  }

  const buckets = NO_BUCKETS();
  let owed = 0;

  const rows = [...byUnit.entries()]
    .map(([unitId, unit]) => {
      const bucket = bucketFor(periodsBetween(unit.oldest, currentPeriod));
      buckets[bucket].units += 1;
      buckets[bucket].owed += unit.owed;
      owed += unit.owed;
      return {
        unitId,
        block: blocks.get(unitId) ?? "",
        termId: terms?.get(unitId) ?? "",
        owed: unit.owed,
        months: unit.months,
        oldest: unit.oldest,
        bucket,
      };
    })
    // Worst first, and by door where two units owe the same, so the list does
    // not reshuffle itself every time a payment lands.
    .sort((one, two) => two.owed - one.owed || compareUnitIds(one.unitId, two.unitId));

  return { rows, buckets, owed };
}

/**
 * The floor a unit id names, when the building writes floors into its doors.
 *
 * Noah's doors read 2A05: the floor first, G for the ground, then the block
 * letter, then the door. An id that is not written that way returns null, and
 * a building of such ids is drawn as a plain grid rather than as floors that
 * would be a guess.
 */
export function floorOf(unitId: string): { label: string; order: number } | null {
  const match = /^(G|\d{1,2})[A-Z]+\d+$/.exec(unitId.toUpperCase());
  if (!match) {
    return null;
  }
  const head = match[1]!;
  return head === "G" ? { label: "G", order: 0 } : { label: head, order: Number(head) };
}

/** The door alone, for a tile that already sits under its block and floor. */
export function doorOf(unitId: string): string {
  const match = /(\d+)$/.exec(unitId);
  return match?.[1] ?? unitId;
}

export interface FloorRow {
  block: string;
  /** What the floor is called on the lift buttons: G, 1, 2. */
  floor: string;
  order: number;
  tiles: UnitTile[];
}

export interface BlockShape {
  block: string;
  /** What this block still owes for the month on screen. */
  owed: Birr;
  /** Top floor first, the way the building stands. */
  floors: FloorRow[];
}

/**
 * The tiles arranged the way the building stands: block by block, each block
 * its floors from the top down to the ground.
 *
 * Null when a tenth of the ids or more name no floor, because a map that
 * silently drops units is worse than a plain grid that shows all of them.
 */
export function buildingShape(tiles: readonly UnitTile[]): BlockShape[] | null {
  if (tiles.length === 0) {
    return [];
  }

  const parsed: { tile: UnitTile; floor: { label: string; order: number } }[] = [];
  for (const tile of tiles) {
    const floor = floorOf(tile.unitId);
    if (floor) {
      parsed.push({ tile, floor });
    }
  }
  if (parsed.length < tiles.length * 0.9) {
    return null;
  }

  const blocks = new Map<string, Map<string, FloorRow>>();
  const owed = new Map<string, number>();
  for (const { tile, floor } of parsed) {
    const floors = blocks.get(tile.block) ?? new Map<string, FloorRow>();
    const row = floors.get(floor.label) ?? {
      block: tile.block,
      floor: floor.label,
      order: floor.order,
      tiles: [],
    };
    row.tiles.push(tile);
    floors.set(floor.label, row);
    blocks.set(tile.block, floors);
    owed.set(tile.block, (owed.get(tile.block) ?? 0) + tile.balance);
  }

  return [...blocks.entries()]
    .sort(([one], [two]) => compareUnitIds(one, two))
    .map(([block, floors]) => ({
      block,
      owed: owed.get(block) ?? 0,
      floors: [...floors.values()]
        .sort((one, two) => two.order - one.order)
        .map((row) => ({
          ...row,
          tiles: [...row.tiles].sort((one, two) => compareUnitIds(one.unitId, two.unitId)),
        })),
    }));
}
